import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useAllChannels, useCreateChannel, useUpdateChannel } from '@/hooks/useChannels';
import { useAllCohorts } from '@/hooks/useCohorts';
import { useAllSections, useCreateSection, useUpdateSection, useDeleteSection } from '@/hooks/useSections';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Pencil, Hash, Lock, Megaphone, MessageSquare, Briefcase, UserPlus, Trash2, FolderOpen } from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type ChannelType = Database['public']['Enums']['channel_type'];

const CHANNEL_TYPE_CONFIG: Record<ChannelType, { label: string; icon: typeof Hash }> = {
  discussion: { label: 'Discussion', icon: MessageSquare },
  announcements: { label: 'Announcements', icon: Megaphone },
  jobs: { label: 'Jobs', icon: Briefcase },
  intros: { label: 'Introductions', icon: UserPlus },
};

export default function AdminChannels() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    channel_type: 'discussion' as ChannelType,
    cohort_id: '',
    section_id: '',
    is_restricted: false,
    is_active: true,
    sort_order: 0,
  });

  // Section management state
  const [isSectionDialogOpen, setIsSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<any>(null);
  const [sectionFormData, setSectionFormData] = useState({
    name: '',
    emoji: '',
    sort_order: 0,
    is_active: true,
  });

  const { data: channels, isLoading } = useAllChannels();
  const { data: cohorts } = useAllCohorts();
  const { data: sections, isLoading: sectionsLoading } = useAllSections();
  const createChannel = useCreateChannel();
  const updateChannel = useUpdateChannel();
  const createSection = useCreateSection();
  const updateSection = useUpdateSection();
  const deleteSection = useDeleteSection();
  const { toast } = useToast();

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      channel_type: 'discussion',
      cohort_id: '',
      section_id: '',
      is_restricted: false,
      is_active: true,
      sort_order: 0,
    });
    setEditingChannel(null);
  };

  const resetSectionForm = () => {
    setSectionFormData({
      name: '',
      emoji: '',
      sort_order: 0,
      is_active: true,
    });
    setEditingSection(null);
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        cohort_id: formData.cohort_id || null,
        section_id: formData.section_id || null,
      };

      if (editingChannel) {
        await updateChannel.mutateAsync({
          id: editingChannel.id,
          ...submitData,
        });
        toast({
          title: 'Channel updated',
          description: `#${formData.slug} has been updated.`,
        });
      } else {
        await createChannel.mutateAsync(submitData);
        toast({
          title: 'Channel created',
          description: `#${formData.slug} has been created.`,
        });
      }
      setIsCreateOpen(false);
      resetForm();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSectionSubmit = async () => {
    try {
      if (editingSection) {
        await updateSection.mutateAsync({
          id: editingSection.id,
          ...sectionFormData,
        });
        toast({
          title: 'Section updated',
          description: `${sectionFormData.name} has been updated.`,
        });
      } else {
        await createSection.mutateAsync(sectionFormData);
        toast({
          title: 'Section created',
          description: `${sectionFormData.name} has been created.`,
        });
      }
      setIsSectionDialogOpen(false);
      resetSectionForm();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    try {
      await deleteSection.mutateAsync(sectionId);
      toast({
        title: 'Section deleted',
        description: 'The section has been removed.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const openEditDialog = (channel: any) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name,
      slug: channel.slug,
      description: channel.description || '',
      channel_type: channel.channel_type,
      cohort_id: channel.cohort_id || '',
      section_id: channel.section_id || '',
      is_restricted: channel.is_restricted,
      is_active: channel.is_active,
      sort_order: channel.sort_order,
    });
    setIsCreateOpen(true);
  };

  const openEditSectionDialog = (section: any) => {
    setEditingSection(section);
    setSectionFormData({
      name: section.name,
      emoji: section.emoji || '',
      sort_order: section.sort_order,
      is_active: section.is_active,
    });
    setIsSectionDialogOpen(true);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getSectionName = (sectionId: string | null) => {
    if (!sectionId) return 'None';
    const section = sections?.find((s) => s.id === sectionId);
    return section ? `${section.emoji || ''} ${section.name}`.trim() : 'None';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Channels & Sections</h1>
        <p className="text-muted-foreground text-sm">
          Manage discussion channels and organize them into sections
        </p>
      </div>

      <Tabs defaultValue="channels">
        <TabsList>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="sections">Sections</TabsTrigger>
        </TabsList>

        <TabsContent value="channels" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isCreateOpen} onOpenChange={(open) => {
              setIsCreateOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Channel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingChannel ? 'Edit Channel' : 'Create New Channel'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingChannel
                      ? 'Update the channel settings below.'
                      : 'Create a new discussion channel.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., General Discussion"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          name: e.target.value,
                          slug: editingChannel ? formData.slug : generateSlug(e.target.value),
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">#</span>
                      <Input
                        id="slug"
                        placeholder="general"
                        value={formData.slug}
                        onChange={(e) =>
                          setFormData({ ...formData, slug: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type">Channel Type</Label>
                    <Select
                      value={formData.channel_type}
                      onValueChange={(value) =>
                        setFormData({ ...formData, channel_type: value as ChannelType })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CHANNEL_TYPE_CONFIG).map(([type, config]) => (
                          <SelectItem key={type} value={type}>
                            <div className="flex items-center gap-2">
                              <config.icon className="h-4 w-4" />
                              {config.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="section">Section</Label>
                    <Select
                      value={formData.section_id || '__none__'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, section_id: value === '__none__' ? '' : value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a section" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No section</SelectItem>
                        {sections?.filter(s => s.is_active).map((section) => (
                          <SelectItem key={section.id} value={section.id}>
                            {section.emoji && `${section.emoji} `}{section.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cohort">Cohort (Optional)</Label>
                    <Select
                      value={formData.cohort_id || '__none__'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, cohort_id: value === '__none__' ? '' : value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Global (all cohorts)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Global (all cohorts)</SelectItem>
                        {cohorts?.map((cohort) => (
                          <SelectItem key={cohort.id} value={cohort.id}>
                            {cohort.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="What is this channel for?"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sort_order">Sort Order</Label>
                    <Input
                      id="sort_order"
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Restricted</Label>
                      <p className="text-sm text-muted-foreground">
                        Only admins/mods can post
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_restricted}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_restricted: checked })
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Active</Label>
                      <p className="text-sm text-muted-foreground">
                        Inactive channels are hidden
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_active: checked })
                      }
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCreateOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={
                      !formData.name ||
                      !formData.slug ||
                      createChannel.isPending ||
                      updateChannel.isPending
                    }
                  >
                    {createChannel.isPending || updateChannel.isPending
                      ? 'Saving...'
                      : editingChannel
                      ? 'Update'
                      : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Channels</CardTitle>
              <CardDescription>
                {channels?.length || 0} channels configured
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Channel</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Section</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {channels?.map((channel) => {
                      const typeConfig = CHANNEL_TYPE_CONFIG[channel.channel_type];
                      const TypeIcon = typeConfig.icon;

                      return (
                        <TableRow key={channel.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Hash className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{channel.name}</span>
                              {channel.is_restricted && (
                                <Lock className="h-3 w-3 text-muted-foreground" />
                              )}
                            </div>
                            {channel.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {channel.description}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              <TypeIcon className="h-3 w-3 mr-1" />
                              {typeConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {getSectionName(channel.section_id)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {channel.cohort_id
                              ? cohorts?.find((c) => c.id === channel.cohort_id)?.name
                              : 'Global'}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                channel.is_active
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }`}
                            >
                              {channel.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(channel)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sections" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isSectionDialogOpen} onOpenChange={(open) => {
              setIsSectionDialogOpen(open);
              if (!open) resetSectionForm();
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Section
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingSection ? 'Edit Section' : 'Create New Section'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingSection
                      ? 'Update the section settings below.'
                      : 'Create a new section to organize channels.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="section-name">Name</Label>
                    <Input
                      id="section-name"
                      placeholder="e.g., Everyone, Cohorts, Industries"
                      value={sectionFormData.name}
                      onChange={(e) =>
                        setSectionFormData({ ...sectionFormData, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="section-emoji">Emoji (Optional)</Label>
                    <Input
                      id="section-emoji"
                      placeholder="e.g., 👥, 🌏, 💼"
                      value={sectionFormData.emoji}
                      onChange={(e) =>
                        setSectionFormData({ ...sectionFormData, emoji: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="section-sort">Sort Order</Label>
                    <Input
                      id="section-sort"
                      type="number"
                      value={sectionFormData.sort_order}
                      onChange={(e) =>
                        setSectionFormData({ ...sectionFormData, sort_order: parseInt(e.target.value) || 0 })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Lower numbers appear first
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Active</Label>
                      <p className="text-sm text-muted-foreground">
                        Inactive sections are hidden
                      </p>
                    </div>
                    <Switch
                      checked={sectionFormData.is_active}
                      onCheckedChange={(checked) =>
                        setSectionFormData({ ...sectionFormData, is_active: checked })
                      }
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsSectionDialogOpen(false);
                      resetSectionForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSectionSubmit}
                    disabled={
                      !sectionFormData.name ||
                      createSection.isPending ||
                      updateSection.isPending
                    }
                  >
                    {createSection.isPending || updateSection.isPending
                      ? 'Saving...'
                      : editingSection
                      ? 'Update'
                      : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Sections</CardTitle>
              <CardDescription>
                {sections?.filter(s => s.is_active).length || 0} active sections
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sectionsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Section</TableHead>
                      <TableHead>Channels</TableHead>
                      <TableHead>Sort Order</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sections?.map((section) => {
                      const channelCount = channels?.filter(c => c.section_id === section.id).length || 0;

                      return (
                        <TableRow key={section.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FolderOpen className="h-4 w-4 text-muted-foreground" />
                              {section.emoji && <span>{section.emoji}</span>}
                              <span className="font-medium">{section.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{channelCount}</Badge>
                          </TableCell>
                          <TableCell>{section.sort_order}</TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                section.is_active
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }`}
                            >
                              {section.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditSectionDialog(section)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSection(section.id)}
                                disabled={deleteSection.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
